import React, { useEffect, useRef, useState, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import ConfirmPrompt from "../../ConfirmationPrompt";
import DialogueBox from "../../DialogueBox";
import {
  deletePartyRequest,
  activateDeactivatePartyRequest,
} from "../../../containers/AdminPanel/features/parties/partiesSlice";
import Icons from "../../../icons";

const CONFIRM_TYPES = {
  ACTIVATE_DEACTIVATE: "activateDeactivate",
  DELETE: "delete",
};

// onShowPartyInfo intentionally omitted — not used internally
const PartyRow = ({ data, editPartyHandler }) => {
  const dispatch = useDispatch();
  const userPermission = useSelector((state) => state.sessionContext?.UserView);

  const canDelete = userPermission?.can_delete_party;
  const canEditActivate = userPermission?.can_create_party;

  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [confirmState, setConfirmState] = useState(null);

  const menuRef = useRef(null);

  const partyId = data?.party_id;
  const isActive = Boolean(data?.is_active);
  const actionLabel = isActive ? "Deactivate" : "Activate";

  const shouldShowDropdown =
    !["HARMONY", "SAMS-FL"].includes(data?.party_source) ||
    data?.party_type === "Public Adjuster";

  const openConfirm = useCallback((payload) => {
    setConfirmState(payload);
    setIsMenuOpen(false);
  }, []);

  const closeConfirm = useCallback(() => setConfirmState(null), []);

  const handleActivateDeactivate = useCallback(() => {
    openConfirm({
      type: CONFIRM_TYPES.ACTIVATE_DEACTIVATE,
      heading: `${actionLabel} party?`,
      message: `Are you sure you want to ${actionLabel.toLowerCase()} this party?`,
      confirmLabel: actionLabel,
      payload: { id: partyId, flag: !isActive },
    });
  }, [isActive, partyId, actionLabel, openConfirm]);

  const handleDelete = useCallback(() => {
    openConfirm({
      type: CONFIRM_TYPES.DELETE,
      heading: "Delete party?",
      message: "Are you sure you want to delete this Party?",
      confirmLabel: "Delete",
    });
  }, [openConfirm]);

  const handleEdit = useCallback(() => {
    setIsMenuOpen(false);
    editPartyHandler?.(data);
  }, [editPartyHandler, data]);

  const toggleDropdown = useCallback(() => {
    setIsMenuOpen((prev) => {
      if (!prev) {
        window.dispatchEvent(new CustomEvent("closePartyMenus", { detail: partyId }));
      }
      return !prev;
    });
  }, [partyId]);

  const handleConfirmAction = useCallback(() => {
    if (!confirmState || !partyId) {
      closeConfirm();
      return;
    }
    if (confirmState.type === CONFIRM_TYPES.ACTIVATE_DEACTIVATE) {
      dispatch(activateDeactivatePartyRequest(confirmState.payload));
    } else if (confirmState.type === CONFIRM_TYPES.DELETE) {
      dispatch(deletePartyRequest({ id: partyId }));
    }
    closeConfirm();
  }, [confirmState, partyId, dispatch, closeConfirm]);

  useEffect(() => {
    if (!isMenuOpen) return;
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setIsMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isMenuOpen]);

  useEffect(() => {
    const handler = (e) => {
      if (e.detail !== partyId) setIsMenuOpen(false);
    };
    window.addEventListener("closePartyMenus", handler);
    return () => window.removeEventListener("closePartyMenus", handler);
  }, [partyId]);

  return (
    <>
      {shouldShowDropdown && (
        <div ref={menuRef} className={`dropDown h__24 noteAction animated superFaster ${isMenuOpen ? 'active' : ''}`}>
          <span
          className="icon"
            
            onClick={toggleDropdown}
          >
            <Icons name="more" className="icon more" />
          </span>

          <div className={`dropDownMenu openRight ${isMenuOpen ? 'onClick' : ''}`}>
            {canEditActivate && (
              <button className="link" type="button" onClick={handleActivateDeactivate}>
                {actionLabel}
              </button>
            )}

              {canEditActivate && (
                <button className="link" type="button" onClick={handleEdit}>
                  Edit
                </button>
              )}

              {canDelete && (
                <button className="link" type="button" onClick={handleDelete}>
                  Delete
                </button>
              )}
            </div>
        </div>
      )}

      <DialogueBox
        overlayClassName="modal"
        contentClassName="modalContent animated faster fadeInUpx2 small"
        shouldCloseOnOverlayClick={false}
        isOpen={Boolean(confirmState)}
        onRequestClose={closeConfirm}
      >
        <ConfirmPrompt
          header={confirmState?.heading}
          message={confirmState?.message}
          confirmationText={confirmState?.confirmLabel}
          cancelText="Cancel"
          onCancelClick={closeConfirm}
          onConfirmClick={handleConfirmAction}
        />
      </DialogueBox>
    </>
  );
};

export default PartyRow;